/**
 * CashCountPanel — Phase-2 step 2 (Full audit). Counts the physical drawer and
 * compares it to the frozen expected cash. Two input modes:
 *   • Per-denomination pad (₱1000…₱1) — each qty × face value, with a live
 *     running grand total shown as you tally.
 *   • Grand total — just type the total counted cash (for shops that count in
 *     one go).
 * Either way the variance vs expected is shown live and saved on the session.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../../contexts/AuthContext';
import { formatPHP } from '../../../lib/utils';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import {
  Wallet, Calculator, Hash, Loader2, CheckCircle2, AlertTriangle, Snowflake,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CashCountPanel({ session, onSaved, onJump }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [method, setMethod] = useState('denomination');
  const [qty, setQty] = useState({});          // { "1000": 3, ... }
  const [grandTotal, setGrandTotal] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${BACKEND_URL}/api/audit-wizard/sessions/${session.id}/cash-count`);
      setData(res.data);
      if (res.data.method) {
        setMethod(res.data.method);
        setQty(res.data.denominations || {});
        if (res.data.method === 'total') setGrandTotal(String(res.data.counted_total ?? ''));
      }
    } catch { setData(null); }
    setLoading(false);
  }, [session.id]);

  useEffect(() => { load(); }, [load]);

  const denoms = data?.denoms || [1000, 500, 200, 100, 50, 20, 10, 5, 1];
  const expected = data?.expected_cash ?? 0;

  const denomTotal = useMemo(
    () => denoms.reduce((sum, d) => sum + d * (parseInt(qty[String(d)], 10) || 0), 0),
    [denoms, qty]);

  const counted = method === 'denomination' ? denomTotal : (parseFloat(grandTotal) || 0);
  const variance = +(counted - expected).toFixed(2);
  const status = variance === 0 ? 'balanced' : variance > 0 ? 'over' : 'short';

  const setDenom = (d, v) => {
    const clean = v.replace(/[^\d]/g, '');
    setQty(prev => ({ ...prev, [String(d)]: clean }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = method === 'denomination'
        ? { method, denominations: Object.fromEntries(denoms.map(d => [String(d), parseInt(qty[String(d)], 10) || 0])) }
        : { method, counted_total: parseFloat(grandTotal) || 0 };
      const res = await api.post(`${BACKEND_URL}/api/audit-wizard/sessions/${session.id}/cash-count`, body);
      toast.success('Cash count saved');
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save count');
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 size={20} className="animate-spin" /></div>;
  }

  // Must freeze before counting (expected cash is the frozen drawer)
  if (data && !data.frozen) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-12" data-testid="cash-count-needs-freeze">
        <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-3"><Snowflake size={20} className="text-amber-500" /></div>
        <p className="text-sm font-semibold text-slate-700">Freeze the snapshot first</p>
        <p className="text-xs text-slate-500 max-w-sm mt-1">The cash count compares the drawer against the frozen expected cash. Close the shift &amp; freeze before counting.</p>
        <button onClick={() => onJump?.('freeze')} className="mt-3 text-sm font-semibold text-[#1A4D2E] hover:underline" data-testid="cash-count-goto-freeze">Go to Freeze →</button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="cash-count-panel">
      {/* Mode toggle */}
      <div className="flex gap-1.5">
        <button onClick={() => setMethod('denomination')} data-testid="cash-mode-denomination"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${method === 'denomination' ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}>
          <Calculator size={13} /> Per denomination
        </button>
        <button onClick={() => setMethod('total')} data-testid="cash-mode-total"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${method === 'total' ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}>
          <Hash size={13} /> Grand total
        </button>
      </div>

      {method === 'denomination' ? (
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-[1fr_auto_1fr] gap-x-3 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-wide text-slate-400">
            <span>Denomination</span><span className="text-center">× Count</span><span className="text-right">Subtotal</span>
          </div>
          {denoms.map(d => {
            const n = parseInt(qty[String(d)], 10) || 0;
            return (
              <div key={d} className="grid grid-cols-[1fr_auto_1fr] gap-x-3 items-center px-4 py-1.5 border-b border-slate-50">
                <span className="text-sm font-semibold text-slate-700">₱{d.toLocaleString()}</span>
                <Input value={qty[String(d)] ?? ''} onChange={e => setDenom(d, e.target.value)}
                  inputMode="numeric" placeholder="0" className="h-8 w-20 text-center font-mono text-sm"
                  data-testid={`cash-denom-${d}`} />
                <span className="text-right font-mono text-sm text-slate-600">{formatPHP(d * n)}</span>
              </div>
            );
          })}
          <div className="grid grid-cols-[1fr_1fr] gap-x-3 items-center px-4 py-2.5 bg-slate-50">
            <span className="text-sm font-semibold text-slate-600">Counted total</span>
            <span className="text-right font-mono font-bold text-[#1A4D2E]" data-testid="cash-counted-total">{formatPHP(denomTotal)}</span>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 p-4">
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Counted grand total</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">₱</span>
            <Input value={grandTotal} onChange={e => setGrandTotal(e.target.value.replace(/[^\d.]/g, ''))}
              inputMode="decimal" placeholder="0.00" className="pl-7 h-11 text-lg font-mono font-bold"
              data-testid="cash-grand-total-input" />
          </div>
        </div>
      )}

      {/* Variance card */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg border border-slate-200 p-3 text-center">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Expected</p>
          <p className="text-sm font-bold text-slate-700 font-mono" data-testid="cash-expected">{formatPHP(expected)}</p>
        </div>
        <div className="rounded-lg border border-slate-200 p-3 text-center">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Counted</p>
          <p className="text-sm font-bold text-slate-700 font-mono" data-testid="cash-counted">{formatPHP(counted)}</p>
        </div>
        <div className={`rounded-lg border p-3 text-center ${status === 'balanced' ? 'border-emerald-200 bg-emerald-50' : status === 'short' ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'}`}>
          <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Variance</p>
          <p className={`text-sm font-bold font-mono flex items-center justify-center gap-1 ${status === 'balanced' ? 'text-emerald-700' : status === 'short' ? 'text-red-600' : 'text-blue-600'}`} data-testid="cash-variance">
            {status === 'balanced' ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
            {variance > 0 ? '+' : ''}{formatPHP(variance)}
          </p>
        </div>
      </div>
      <p className="text-xs text-center text-slate-400">
        {status === 'balanced' ? 'Drawer matches the frozen expected cash.'
          : status === 'short' ? `Drawer is short ${formatPHP(Math.abs(variance))} vs expected.`
          : `Drawer is over ${formatPHP(variance)} vs expected.`}
      </p>

      <div className="flex justify-end pt-2 border-t border-slate-100">
        <Button onClick={save} disabled={saving} data-testid="cash-count-save-btn"
          className="bg-[#1A4D2E] hover:bg-[#14532d] text-white h-10 px-6">
          {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Wallet size={15} className="mr-1.5" />}
          {data?.method ? 'Update count' : 'Save count'}
        </Button>
      </div>
    </div>
  );
}
